import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ProjectsFeatureService, TimesheetsFeatureService } from '@features';
import { MatExpansionModule } from '@angular/material/expansion';
import { PhasesComponent } from './component/phases/phases.component';
import { Project, ProjectDomain } from '@domain';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ProjectFiltersComponent } from './component/project-filters/project-filters.component';
import { HighlightPipe } from '../../../utils/highlight.pipe';

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [MatExpansionModule, PhasesComponent, MatButtonModule, MatProgressSpinnerModule, MatIconModule, ProjectFiltersComponent, HighlightPipe],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.css',
})
export class ProjectsComponent implements OnInit, OnDestroy {
  protected readonly Object = Object;
  _projectFeature = inject(ProjectsFeatureService);
  _timesheetFeature = inject(TimesheetsFeatureService);

  projectSubscription?: Subscription;
  phaseSubscription?: Subscription;
  taskSubscription?: Subscription;

  /** Manual expand/collapse from the toolbar buttons */
  isExpandedUser = false;

  constructor() {}

  ngOnInit() {
    this.projectSubscription = this._projectFeature.fetchProjects().subscribe();
    this.phaseSubscription = this._projectFeature.fetchPhases().subscribe();
    this.taskSubscription = this._projectFeature.fetchTasks().subscribe();
  }

  ngOnDestroy() {
    this.projectSubscription?.unsubscribe();
    this.phaseSubscription?.unsubscribe();
    this.taskSubscription?.unsubscribe();
  }

  protected readonly ProjectDomain = new ProjectDomain();

  handleOnClickAddHours(): void {
    this._timesheetFeature.resetTimesheetLinesFilter();
    this._timesheetFeature.handleOpeningAddingFillHourDialog();
  }

  /** Pure getter: no mutations */
  get filteredProjects(): Project[] {
    const { projectNo } = this._projectFeature.filterProjects();
    const search = this._projectFeature.searchText().toLowerCase();
    let projects = this._projectFeature.projects();

    if (projectNo?.length) {
      projects = projects.filter((p) => projectNo.includes(p.projectNo));
    }

    if (search) {
      const phases = this._projectFeature.phases();
      const tasks = this._projectFeature.tasks();
      projects = projects.filter((project) => {
        if (project.projectNo.toLowerCase().includes(search) || project.projectName.toLowerCase().includes(search)) return true;
        const projectPhases = phases.filter((p) => p.projectNo === project.projectNo);
        return projectPhases.some((phase) => {
          if (
            phase.phaseNo.toLowerCase().includes(search) ||
            phase.phaseName.toLowerCase().includes(search) ||
            (phase.workItemId != null && phase.workItemId.toString().includes(search))
          ) return true;
          return tasks
            .filter((t) => t.projectNo === project.projectNo && t.phaseNo === phase.phaseNo)
            .some(
              (task) =>
                task.taskNo.toLowerCase().includes(search) ||
                task.taskName.toLowerCase().includes(search) ||
                (task.workItemId != null && task.workItemId.toString().includes(search)),
            );
        });
      });
    }

    return projects;
  }

  /** Auto-expand panels when there’s an active filter (pure, no mutations) */
  get autoExpand(): boolean {
    const { projectNo } = this._projectFeature.filterProjects();
    return !!projectNo?.length || !!this._projectFeature.searchText();
  }

  /** Final expanded state = manual toggle OR filter-driven auto-expand */
  get isExpanded(): boolean {
    return this.isExpandedUser || this.autoExpand;
  }
}
